import React, { useEffect, useState, useRef, useCallback } from 'react'
import { getSnakeLeaderboard } from '../../../../../../service/SnakeService'
import { useAuth } from '../../../../../../contexts/AuthContext'
import styles from './SnakeLeaderboard.module.scss'

const API_BASE = 'https://security.ncore.kg'
// Estimated card width (px) + gap — used to calculate how many fit
const CARD_W = 154
const SEP_W  = 36

function getAvatarUrl(avatar, username) {
  if (!avatar) return `https://ui-avatars.com/api/?name=${encodeURIComponent(username || 'U')}&background=2a1a4a&color=ce9eff&bold=true&size=40`
  if (avatar.startsWith('http')) return avatar
  return `${API_BASE}${avatar}`
}

function RankCard({ entry, rank, isMe }) {
  return (
    <div className={`${styles.card} ${isMe ? styles.me : ''}`}>
      <span className={styles.rank}>#{rank}</span>
      <img
        src={getAvatarUrl(entry.avatar, entry.username)}
        alt={entry.username}
        className={styles.avatar}
        onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(entry.username || 'U')}&background=2a1a4a&color=ce9eff&bold=true&size=40` }}
      />
      <div className={styles.info}>
        <span className={styles.username}>{isMe ? 'Вы' : entry.username}</span>
        <span className={styles.score}>{entry.best_score}</span>
      </div>
      <div className={styles.tooltip}>
        <img
          src={getAvatarUrl(entry.avatar, entry.username)}
          alt={entry.username}
          className={styles.tooltipAvatar}
          onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(entry.username || 'U')}&background=2a1a4a&color=ce9eff&bold=true&size=48` }}
        />
        <span className={styles.tooltipRank}>#{rank}</span>
        <span className={styles.tooltipName}>{entry.username}</span>
        <span className={styles.tooltipScore}>{entry.best_score} pts</span>
      </div>
    </div>
  )
}

export default function SnakeLeaderboard() {
  const [board, setBoard] = useState([])
  const [loading, setLoading] = useState(true)
  const [rowWidth, setRowWidth] = useState(0)
  const { user, isAuthenticated } = useAuth()
  const rowRef = useRef(null)

  useEffect(() => {
    const fetchBoard = () => {
      getSnakeLeaderboard()
        .then(data => setBoard(Array.isArray(data) ? data : []))
        .catch(() => setBoard([]))
        .finally(() => setLoading(false))
    }
    fetchBoard()
    const interval = setInterval(fetchBoard, 60_000)
    return () => clearInterval(interval)
  }, [])

  const recalc = useCallback(() => {
    if (!rowRef.current) return
    setRowWidth(rowRef.current.offsetWidth)
  }, [])

  useEffect(() => {
    recalc()
    const ro = new ResizeObserver(recalc)
    if (rowRef.current) ro.observe(rowRef.current)
    return () => ro.disconnect()
  }, [recalc, board])

  if (loading || board.length === 0) return null

  // How many cards fit with no reservation
  const fullCount = rowWidth > 0 ? Math.max(1, Math.floor(rowWidth / CARD_W)) : 20
  // How many cards fit when we reserve space for «…» + userCard + «…» + nextCard
  const reservedCount = rowWidth > 0 ? Math.max(1, Math.floor((rowWidth - SEP_W - CARD_W - SEP_W - CARD_W) / CARD_W)) : 16

  const myIndex = isAuthenticated && user
    ? board.findIndex(e => e.username === user.username)
    : -1
  const myEntry = myIndex >= 0 ? board[myIndex] : null
  const myRank  = myIndex >= 0 ? myIndex + 1 : null

  // User fits naturally → show full row (fills all available space)
  // User is outside → show reduced top + separator + user + separator + next
  const myInFull = myIndex >= 0 && myIndex < fullCount
  const displayCount = myInFull ? fullCount : reservedCount
  const topEntries = board.slice(0, displayCount)

  const handlePlay = () => window.dispatchEvent(new CustomEvent('launchSnakeGame'))

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <span className={styles.icon}>🐍</span>
        <span className={styles.title}>Рейтинг Змейки</span>
        <button className={styles.playBtn} onClick={handlePlay} title="Запустить змейку">
          ▶ Играть
        </button>
      </div>

      <div className={styles.row} ref={rowRef}>
        {topEntries.map(entry => {
          const isMe = isAuthenticated && user && entry.username === user.username
          return <RankCard key={entry.username} entry={entry} rank={entry.rank} isMe={isMe} />
        })}

        {/* user is outside visible range — show separator + their card + separator + next */}
        {isAuthenticated && myEntry && !myInFull && (
          <>
            <div className={styles.separator}>…</div>
            <RankCard entry={myEntry} rank={myRank} isMe={true} />
            {board[myIndex + 1] && (
              <>
                <div className={styles.separator}>…</div>
                <RankCard entry={board[myIndex + 1]} rank={myRank + 1} isMe={false} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
